import React, { useState, useEffect } from "react";
import {
  Button,
  Input,
  Col,
  Row,
  Container,
  Table,
  FormGroup,
} from "reactstrap";
import "./index.css";
import ApiCaller from "api/apiCaller";
import Loading from "components/Loading";
import api from "api";
import { UserInfo } from "model/user";
import { ApiResponse } from "model/response";
import ReactPaginate from "react-paginate";
import {
  Dictionary,
  DictionaryListingResponse,
} from "model/dictionary";
import _ from "lodash";
import EditbaleInput from "components/EditableInput";

const PAGE_LIMIT = 50;
interface ListingData {
  dictionaries: Array<Dictionary>;
  page: number;
  total: number;
}

export default function DictionarySearching() {
  const companyId: any = api.getCurrentCompanyId();
  const [currentUser, setCurrentUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [listingData, setListingData] = useState<ListingData>({
    dictionaries: [],
    page: 0,
    total: 0,
  });
  const [displayMessage, setDisplayMessage] = useState<string>("");
  const canEditDictionary = currentUser?.is_admin || currentUser?.is_company_admin || currentUser?.is_data_practitioner;
  const canViewChangelog = currentUser?.is_admin || currentUser?.is_company_admin;

  const initData = async () => {
    const user = api.getCurrentUser();
    setCurrentUser(user);
  };

  useEffect(() => {
    initData();
  }, []);

  async function fetchData() {
    setLoading(true);
    let url = `search/dictionary?company_id=${companyId}&limit=${PAGE_LIMIT}&page=${listingData?.page}`;
    if (searchKeyword !== "") {
      url = url + `&keyword=${searchKeyword}`;
      const res: ApiResponse = await ApiCaller.get(url);
      if (res?.status === "ok") {
        if (res.data?.total > 0) {
          const data: DictionaryListingResponse = res?.data;
          setListingData({
            ...listingData,
            ...{ dictionaries: data?.dictionaries, total: data?.total },
          });
        } else {
          setListingData({ dictionaries: [], page: 0, total: 0});
          setDisplayMessage("Couldn't find what you're looking. Try another keyword");
        }
      }
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchData();
  }, [displayMessage, listingData?.page]);

  if (loading) return <Loading />;

  return (
    <Container className="dictionary-search">
      <Row>
        <Col xs={12}>
          <h1>Data Dictionary</h1>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <br />
          <FormGroup>
            <Input
              onKeyPress={(e: any)=>{
                if (e.code == 'Enter') {
                  fetchData()
                }
              }}
              style={styles.searchBox}
              placeholder="Search by name, code_name, description, ... of dataset, table or column"
              onChange={(e) => {
                setSearchKeyword(e.target.value);
                setListingData({
                  ...listingData,
                  ...{ page: 0 },
                });
              }}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <Button
            color="primary"
            onClick={() => {
              fetchData();
            }}
          >
            Search
          </Button>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <ReactPaginate
            previousLabel={"<"}
            nextLabel={">"}
            breakLabel={"..."}
            breakClassName={"break-me"}
            pageCount={Math.floor(listingData?.total / PAGE_LIMIT) + 1}
            marginPagesDisplayed={3}
            pageRangeDisplayed={2}
            initialPage={0}
            onPageChange={async (page) => {
              setListingData({
                ...listingData,
                ...{ page: page.selected },
              });
            }}
            containerClassName={"pagination"}
            activeClassName={"active"}
          />
        </Col>
      </Row>
      {loading ? <Loading /> :
      <Row>
        {listingData.dictionaries.length > 0 ? (
          <Col xs={12}>
            <br />
            <Table responsive striped>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Datasource</th>
                  <th>Dataset</th>
                  <th>Table</th>
                  <th>Description</th>
                  <th>Calculation Note</th>
                  <th>Last Edit By</th>
                  {canViewChangelog && <th></th>}
                </tr>
              </thead>
              <tbody>
                {listingData.dictionaries.map((dictionary) => {
                  return (
                    <tr key={`${dictionary.index}-${dictionary.id}`}>
                      <td>
                        <a
                          href={`/${
                            dictionary.index == "column"
                              ? "table"
                              : dictionary.index
                          }/${
                            dictionary.index == "column"
                              ? dictionary.table_id
                              : dictionary?.id
                          }`}
                        >
                          <b><span style={styles.name}>{dictionary.name}</span></b>
                        </a>
                        <br/>
                        {dictionary.labels &&
                          Object.keys(dictionary.labels).map(key => (
                            <span key={key} className="item__label">{key}:<b>{dictionary.labels[key]}</b><br/></span>
                          ))
                        }
                      </td>
                      <td>{dictionary.index}</td>
                      <td>{dictionary.datasource_name}</td>
                      <td>
                        <a href={`/dataset/${dictionary?.dataset_id}`}>
                          <span style={styles.name}>{dictionary.dataset_name}</span>
                        </a>
                      </td>
                      <td>
                        <a href={`/table/${dictionary?.table_id}`}>
                          <span style={styles.name}>{dictionary.table_name}</span>
                        </a>
                      </td>
                      <td className='custom-textarea-multi-line'>
                        {canEditDictionary ?
                          <EditbaleInput
                            index={dictionary.index}
                            id={dictionary.id}
                            updateKey={"description"}
                            updateValue={dictionary?.description}
                          /> : <span>{dictionary.description}</span>
                        }
                      </td>
                      <td className='custom-textarea-multi-line'>
                        {(canEditDictionary && dictionary.index === "column") ?
                          <EditbaleInput
                            index={dictionary.index}
                            id={dictionary.id}
                            updateKey={"calculation_note"}
                            updateValue={dictionary?.calculation_note}
                          /> : <span>{dictionary.calculation_note}</span>
                        }
                      </td>
                      <td>{dictionary.last_edit_by_email}</td>
                      {canViewChangelog &&
                        <td>
                          <a href={`/dictionary/changelog/${dictionary.index}/${dictionary.id}`}>
                            changelog
                          </a>
                        </td>
                      }
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Col>
        ) : (
          <Col xs={12}>
            <br />
            <br />
            <p>{displayMessage}</p>
          </Col>
        )}
      </Row>
    }
    </Container>
  );
}

const styles: { [name: string]: React.CSSProperties } = {
  searchBox: {
    fontSize: 16,
    padding: 16,
    height: 48,
  },
  filter: {
    fontSize: 16,
    padding: 10,
    height: 38,
  },
};
